/**
 * AGENT INSTALLER
 * 
 * Modal wizard com os seguintes componentes:
 * - AgentInstallerHeader: Header/perfil do agente
 * - AgentInstallerProgress: Barras de progresso
 * - AgentInstallerStepRenderer: Renderização dos steps
 * - useAgentInstallerLogic: Lógica de backend
 */

import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/infrastructure/lib/utils';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { AgentInstallerProps } from '../../types/AgentInstaller';

interface AgentInstallerWizardProps extends AgentInstallerProps {
  onRefreshAgentList?: () => Promise<void>;
}
import { AgentInstallerHeader } from './AgentInstallerHeader';
import { AgentInstallerProgress } from './AgentInstallerProgress';
import { AgentInstallerStepRenderer } from './AgentInstallerStepRenderer';
import { useAgentInstallerLogic } from '@/features/Agents/hooks/useAgentInstallerLogic';
import { useTranslation } from 'react-i18next';

export const AgentInstallerWizard: React.FC<AgentInstallerWizardProps> = ({
  agent,
  isOpen,
  onClose,
  onInstallComplete,
  onRefreshAgentList
}) => {
  // Hook customizado com toda a lógica do wizard
  const {
    wizardState,
    validateCurrentStep,
    handleNext,
    handlePrevious,
    handleInstall,
    handleCancel,
    updateSelectedIntegrations,
    updateParameterValues,
    updatePromptValues,
  } = useAgentInstallerLogic({ 
    agent, 
    onInstallComplete, 
    onClose,
    onRefreshAgentList
  });

  // Dados calculados
  const totalSteps = wizardState.steps.length;
  const currentStepData = wizardState.steps[wizardState.currentStep];
  const isLastStep = wizardState.currentStep === totalSteps - 1;
  const isFirstStep = wizardState.currentStep === 0;
  const canProceed = validateCurrentStep();
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] min-h-[600px] flex flex-col p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>
            {t('installation.installationInProgress')} {agent.name}
          </DialogTitle>
          <DialogDescription>
            {t('installation.wizardDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Header com perfil do agente */}
        <AgentInstallerHeader agent={agent} />

        {/* Progress Indicator e Barra de Progresso */}
        <AgentInstallerProgress
          currentStep={wizardState.currentStep}
          totalSteps={totalSteps}
          steps={wizardState.steps}
          installationProgress={wizardState.installationProgress}
          isInstalling={wizardState.isInstalling}
          installationComplete={wizardState.installationComplete}
        />

        {/* Conteúdo do Step Atual */}
        {currentStepData && (
          <AgentInstallerStepRenderer
            step={currentStepData}
            agent={agent}
            wizardState={wizardState}
            onUpdateSelectedIntegrations={updateSelectedIntegrations}
            onUpdateParameterValues={updateParameterValues}
            onUpdatePromptValues={updatePromptValues}
          />
        )}

        {/* Footer com botões de navegação */}
        <DialogFooter className="px-6 py-4 border-t bg-background rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            {/* Botão Cancelar - só aparece quando instalação NÃO está completa */}
            {!wizardState.installationComplete && (
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="text-muted-foreground hover:text-foreground"
              >
                {t('installation.cancel')}
              </Button>
            )}
            
            <div className="flex gap-2 ml-auto">
              {!isFirstStep && !wizardState.isInstalling && !wizardState.installationComplete && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={wizardState.isInstalling}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('installation.previousStep')}
                </Button>
              )}

              {!wizardState.installationComplete && (
                <>
                  {isLastStep ? (
                    <Button
                      onClick={handleInstall}
                      disabled={!canProceed || wizardState.isInstalling}
                      className={cn(
                        wizardState.isInstalling && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {wizardState.isInstalling ? (
                        <>
                          <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          {t('installation.installing')} {Math.round(wizardState.installationProgress.percentage)}%
                        </>
                      ) : (
                        t('installation.install')
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed || wizardState.isInstalling}
                    >
                      {t('installation.nextStep')}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </>
              )}

              {/* Botão Fechar - só aparece quando instalação está completa */}
              {wizardState.installationComplete && (
                <Button onClick={handleCancel}>
                  {t('installation.close')}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
